import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Mail, Lock, Check, Eye, EyeOff, Upload } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const availableRoles = ["Admin", "user"];

export default function NewUserTab() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [availablePlans, setAvailablePlans] = useState([]);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    Confirmpassword: "",
    plan: "",
    role: "",
    photo: null,
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmpassword, setShowConfirmpassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [plansError, setPlansError] = useState(null);
  const fileInputRef = useRef(null); // Ref to reset file input
  const BASE_URL = 'https://backend.emerald.azadnoori.com';

  useEffect(() => {
    if (!user || !token) {
      navigate("/login");
      return;
    }
    if (user.role !== "Admin" && user.role !== "Super Admin") {
      navigate("/");
    }
  }, [user, token, navigate]);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const fetchPlans = async () => {
      setIsLoadingPlans(true);
      setPlansError(null);
      try {
        const res = await axios.get(`${BASE_URL}/api/v1/plans`, {
          signal: controller.signal,
        });
        if (mounted) {
          const plans = res.data.data.Plan || [];
          const validPlans = plans.map((plan) => ({
            id: plan.id,
            name: typeof plan.name === "string" ? plan.name : JSON.stringify(plan.name),
          }));
          setAvailablePlans(validPlans);
        }
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        if (mounted) {
          if (error.response?.status === 401) {
            setPlansError("Session expired. Please log in again.");
            navigate("/login");
          } else {
            const errorMessage =
              error.response?.status === 429
                ? `Too many requests. Please try again later.${
                    error.response?.headers["retry-after"]
                      ? ` Retry after ${error.response.headers["retry-after"]} seconds.`
                      : ""
                  }`
                : error.response?.data?.message || "Failed to fetch plans.";
            setPlansError(errorMessage);
          }
        }
      } finally {
        if (mounted) {
          setIsLoadingPlans(false);
        }
      }
    };

    fetchPlans();
    return () => {
      mounted = false;
      controller.abort();
    };
  }, [navigate, BASE_URL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
    setApiError(null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors({ ...errors, photo: "Please upload an image file" });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, photo: "Image size must be less than 5MB" });
        return;
      }
      // Revoke previous preview URL to prevent memory leaks
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, photo: file });
      setPhotoPreview(previewUrl);
      setErrors({ ...errors, photo: null });
      setApiError(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstname.trim()) newErrors.firstname = "First name is required";
    if (!formData.lastname.trim()) newErrors.lastname = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (formData.password !== formData.Confirmpassword) {
      newErrors.Confirmpassword = "Passwords do not match";
    }
    if (!formData.plan) newErrors.plan = "Please select a plan";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(true);
    setApiError(null);
    try {
      const data = new FormData();
      data.append("firstname", formData.firstname);
      data.append("lastname", formData.lastname);
      data.append("email", formData.email);
      data.append("password", formData.password);
      data.append("Confirmpassword", formData.Confirmpassword);
      data.append("plan", formData.plan);
      data.append("role", formData.role);
      if (formData.photo) {
        data.append("profileImage", formData.photo);
      }

      const response = await axios.post(
        `${BASE_URL}/api/v1/users/addUser`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsSubmitting(false);
      setSuccess(true);
      setTimeout(() => {
        setFormData({
          firstname: "",
          lastname: "",
          email: "",
          password: "",
          Confirmpassword: "",
          plan: "",
          role: "",
          photo: null,
        });
        if (photoPreview) {
          URL.revokeObjectURL(photoPreview); // Clean up
        }
        setPhotoPreview(null);
        setSuccess(false);
        localStorage.setItem("userAdded", Date.now().toString());
        setTimeout(() => localStorage.removeItem("userAdded"), 1000);
        if (fileInputRef.current) {
          fileInputRef.current.value = ""; // Reset file input
        }
      }, 3000);
    } catch (error) {
      setIsSubmitting(false);
      if (error.response?.status === 401) {
        setApiError("Session expired. Please log in again.");
        navigate("/login");
      } else {
        const errorMessage =
          error.response?.data?.message || "An error occurred while creating the user.";
        setApiError(errorMessage);
      }
    }
  };

  const formVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Add New User</h2>

      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="bg-emerald-100 border border-emerald-200 text-emerald-800 rounded-lg p-4 flex items-center mb-6"
          >
            <Check className="mr-2 text-emerald-600" size={20} />
            <span>User has been successfully created!</span>
          </motion.div>
        )}

        {apiError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-red-100 border border-red-200 text-red-800 rounded-lg p-4 mb-6"
          >
            <span>{apiError}</span>
          </motion.div>
        )}

        {plansError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-red-100 border border-red-200 text-red-800 rounded-lg p-4 mb-6"
          >
            <span>{plansError}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.form
        onSubmit={handleSubmit}
        variants={formVariants}
        initial="hidden"
        animate="visible"
        className="bg-white rounded-xl shadow-lg overflow-hidden p-6"
      >
        <motion.div variants={itemVariants} className="mb-6 flex flex-col items-center">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profile Photo (Optional)
          </label>
          <div className="relative h-24 w-24 mb-4 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 p-1 transition-transform hover:scale-105">
            <div className="h-full w-full rounded-full bg-white flex items-center justify-center overflow-hidden">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Profile preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                <User className="text-gray-400" size={40} />
              )}
            </div>
          </div>
          <label className="cursor-pointer bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-emerald-700 transition-colors">
            <Upload size={18} />
            Upload Photo
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
              ref={fileInputRef}
            />
          </label>
          {errors.photo && <p className="mt-2 text-sm text-red-600">{errors.photo}</p>}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <motion.div variants={itemVariants}>
            <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <div className="relative">
              <input
                id="firstname"
                name="firstname"
                type="text"
                value={formData.firstname}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                  errors.firstname ? "border-red-500" : ""
                }`}
                placeholder="John"
              />
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            </div>
            {errors.firstname && <p className="mt-1 text-sm text-red-600">{errors.firstname}</p>}
          </motion.div>

          <motion.div variants={itemVariants}>
            <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <div className="relative">
              <input
                id="lastname"
                name="lastname"
                type="text"
                value={formData.lastname}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                  errors.lastname ? "border-red-500" : ""
                }`}
                placeholder="Doe"
              />
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            </div>
            {errors.lastname && <p className="mt-1 text-sm text-red-600">{errors.lastname}</p>}
          </motion.div>
        </div>

        <motion.div variants={itemVariants} className="mb-6">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <div className="relative">
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                errors.email ? "border-red-500" : ""
              }`}
              placeholder="john.doe@example.com"
            />
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <motion.div variants={itemVariants}>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                  errors.password ? "border-red-500" : ""
                }`}
                placeholder="••••••••"
              />
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
          </motion.div>

          <motion.div variants={itemVariants}>
            <label
              htmlFor="Confirmpassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="Confirmpassword"
                name="Confirmpassword"
                type={showConfirmpassword ? "text" : "password"}
                value={formData.Confirmpassword}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                  errors.Confirmpassword ? "border-red-500" : ""
                }`}
                placeholder="••••••••"
              />
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowConfirmpassword(!showConfirmpassword)}
              >
                {showConfirmpassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.Confirmpassword && (
              <p className="mt-1 text-sm text-red-600">{errors.Confirmpassword}</p>
            )}
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <motion.div variants={itemVariants}>
            <label htmlFor="plan" className="block text-sm font-medium text-gray-700 mb-1">
              Plan
            </label>
            <select
              id="plan"
              name="plan"
              value={formData.plan}
              onChange={handleInputChange}
              className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 ${
                errors.plan ? "border-red-500" : ""
              }`}
              disabled={isLoadingPlans}
            >
              <option value="">Select a plan</option>
              {availablePlans.map((plan) => (
                <option key={plan.id} value={plan.name}>
                  {plan.name}
                </option>
              ))}
            </select>
            {isLoadingPlans && <p className="mt-1 text-sm text-gray-600">Loading plans...</p>}
            {errors.plan && <p className="mt-1 text-sm text-red-600">{errors.plan}</p>}
          </motion.div>

          <motion.div variants={itemVariants}>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
            >
              <option value="">Select a role</option>
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </motion.div>
        </div>

        <motion.div variants={itemVariants} className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting || isLoadingPlans}
            className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium flex items-center justify-center min-w-[120px] hover:bg-emerald-700 transition-colors disabled:bg-emerald-400"
          >
            {isSubmitting ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Number.POSITIVE_INFINITY, duration: 1, ease: "linear" }}
              >
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </motion.div>
            ) : (
              "Create User"
            )}
          </button>
        </motion.div>
      </motion.form>
    </div>
  );
}